import ListView from '@/components/datagrid/ListView';
import useApiQuery from '@/hooks/useApiQuery';
import { Box, useTheme } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/ApiService';
import { toast } from 'react-toastify';
import ContractEditDialog from './contract/ContractEditDialog';
import MassActionDialog from './MassActionDialog';
import useApiDelete from '@/hooks/useApiDelete';
import { Delete, DocumentScanner, Add } from '@mui/icons-material';
import useLiveData from '@/hooks/useLiveData';
import useTitle from '@/hooks/useTitle';
import useSearchStore from '@/store/search';
import ContractTimeSelector from './contract/content/ContractTimeSelector';
import { useRouteLoaderData } from 'react-router-dom';

const columns = [
  {
    field: 'partyName',
    headerName: 'Kunde',
    flex: 1,
    renderCell: (params) => (
      <Box sx={{ fontWeight: 'bold' }}>{params.value}</Box>
    ),
  },
  {
    field: 'contractNumber',
    headerName: 'Vertragsnummer',
    flex: 1,
  },
  {
    field: 'title',
    headerName: 'Bezeichnung',
    flex: 1,
  },
  {
    field: 'description',
    headerName: 'Beschreibung',
    flex: 1,
  },
  {
    field: 'lastAmount',
    headerName: 'Letzter Betrag',
    width: 140,
    valueGetter: (value) =>
      value?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }),
  },
  {
    field: 'billingFrequency',
    headerName: 'Abrechnungsintervall',
    width: 180,
    valueGetter: (value) =>
      value === 'no_billing' ? 'Keine Abrechnung' : value,
  },
];

export default function ContractsPage() {
  useTitle(`Verträge`);
  const theme = useTheme();
  const [selectedRows, setSelectedRows] = useState([]);
  const [isMassActionOpen, setIsMassActionOpen] = useState(false);
  const [pstDate, setPstDate] = useState(null);
  const navigate = useNavigate();

  const {
    fixedDeploymentConfigs: { contracts: contractsConfig },
  } = useRouteLoaderData('auth');

  const [searchQuery, setSearchQuery] = useState('');
  const [matchedPartyIds, setMatchedPartyIds] = useState([]);
  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();

  useEffect(() => {
    registerLocalSearch({
      key: 'contracts',
      label: 'Verträge suchen',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });
    return () => {
      unregisterLocalSearch();
    };
  }, [registerLocalSearch, unregisterLocalSearch]);

  useEffect(() => {
    if (!searchQuery) {
      setMatchedPartyIds([]);
      return;
    }

    let cancelled = false;
    const lookupParties = async () => {
      try {
        const { data } = await api.get(
          `/party?customerNumber=${encodeURIComponent(searchQuery)}&limit=50`,
        );
        if (!cancelled) {
          const ids = (data?.result || []).map((p) => p._id);
          setMatchedPartyIds(ids);
        }
      } catch {
        if (!cancelled) setMatchedPartyIds([]);
      }
    };

    const timeout = setTimeout(lookupParties, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchQuery]);

  const mongoFilter = useMemo(() => {
    const filter = { $and: [] };

    if (pstDate) {
      const year = pstDate.getFullYear();
      const month = String(pstDate.getMonth() + 1).padStart(2, '0');
      const day = String(pstDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      filter.$and.push({
        contractItems: {
          $elemMatch: {
            pricingDate: {
              $lt: dateString,
            },
          },
        },
      });
    }

    if (searchQuery) {
      const orConditions = [
        { partyName: { $regex: searchQuery, $options: 'i' } },
        { contractNumber: { $regex: searchQuery, $options: 'i' } },
        { title: { $regex: searchQuery, $options: 'i' } },
      ];

      if (matchedPartyIds.length > 0) {
        orConditions.push({ partyId: { $in: matchedPartyIds } });
      }

      filter.$and.push({ $or: orConditions });
    }

    if (filter.$and.length === 0) {
      return {};
    }

    return filter;
  }, [searchQuery, pstDate, matchedPartyIds]);

  const {
    data: contracts,
    isPending,
    refetch,
  } = useApiQuery('contract', mongoFilter, true);

  useLiveData('contract');
  const { mutateAsync: deleteContract } = useApiDelete('contract');

  const [isNewContractDialogOpen, setIsNewContractDialogOpen] = useState(false);

  const handleCreateContract = async (newContractData) => {
    const toastId = toast.loading('Vertrag wird erstellt...');
    try {
      const { data: newContract } = await api.post(
        '/contract',
        newContractData,
      );

      toast.update(toastId, {
        render: 'Vertrag erfolgreich erstellt!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      setIsNewContractDialogOpen(false);
      refetch();
      navigate(`/billing/contracts/${newContract.insertedId}`);
    } catch (error) {
      console.error('Fehler beim Erstellen des Vertrags:', error);
      toast.update(toastId, {
        render: 'Fehler beim Erstellen des Vertrags.',
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const actions = [
    {
      label: 'Vertrag',
      icon: Add,
      onClick: () => {
        setIsNewContractDialogOpen(true);
      },
    },
    {
      label: 'Abrechnen',
      icon: DocumentScanner,
      disabled: selectedRows.length === 0,
      onClick: () => {
        toast.info(
          'Massenabrechnung ist noch in der entwicklung. Bitte Verträge einzeln abrechnen.',
        );
      },
    },
  ];
  contractsConfig.showNoBillingContractMassImport &&
    actions.push({
      label: 'Massenaktion',
      icon: DocumentScanner,
      onClick: () => {
        setIsMassActionOpen(true);
      },
    });
  actions.push(
    <Box key="spacer-1" sx={{ flexGrow: 1 }} />,
    <ContractTimeSelector
      label="Datum"
      date={pstDate}
      onDateChange={setPstDate}
      theme={theme}
      size="small"
      minWidth={200}
    />,
    {
      title: 'Löschen',
      label: 'Löschen',
      color: 'error',
      selection: 'multi',
      icon: Delete,
      onClick: async () => {
        const toastId = toast.loading(
          `${selectedRows.length > 1 ? 'Verträge werden' : 'Vertrag wird'} gelöscht...`,
        );
        try {
          const deletePromises = selectedRows.map((rowId) =>
            deleteContract(rowId),
          );
          await Promise.all(deletePromises);
          await refetch();
          setSelectedRows([]);
          toast.update(toastId, {
            render: `${
              selectedRows.length > 1 ? 'Verträge wurden' : 'Vertrag wurde'
            } gelöscht!`,
            type: 'success',
            isLoading: false,
            autoClose: 3000,
            closeButton: true,
          });
        } catch (error) {
          toast.update(toastId, {
            render: 'Fehler beim Löschen der Verträge.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
            closeButton: true,
          });
        }
      },
    },
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          flex: '1',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <ListView
          key={JSON.stringify(mongoFilter)}
          rows={contracts || []}
          loading={isPending}
          columns={columns}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
          actions={actions}
          onRowClick={(row) => {
            navigate(`/billing/contracts/${row.id}`);
          }}
          refetch={refetch}
        />
      </Box>

      <ContractEditDialog
        open={isNewContractDialogOpen}
        onClose={() => setIsNewContractDialogOpen(false)}
        onSave={handleCreateContract}
      />
      <MassActionDialog
        open={isMassActionOpen}
        onClose={() => setIsMassActionOpen(false)}
      />
    </Box>
  );
}
